import {
  AlertTriangle,
  BookOpenCheck,
  Clock,
  FileText,
  GitFork,
  MapPin,
  Mic,
  Plane,
  Sun,
  Video,
} from "lucide-react";
import { fmtTime, isAllDay, sessionLabel, timeRange, typeLabel } from "@/lib/eventMeta";
import { Markdown } from "@/components/Markdown";
import { useForumColors } from "@/context/ForumColorContext";
import { alpha } from "@/lib/forumColors";

const Action = ({ testId, label, icon: Icon, onPick }) => (
  <span
    role="button"
    tabIndex={0}
    data-testid={testId}
    onClick={(e) => {
      e.stopPropagation();
      onPick();
    }}
    onKeyDown={(e) => {
      if (e.key === "Enter") {
        e.stopPropagation();
        onPick();
      }
    }}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-sm font-semibold hover:bg-accent transition-colors duration-200"
  >
    <Icon className="h-4 w-4" /> {label}
  </span>
);

export const EventRow = ({
  event,
  onClick,
  onVoiceLog,
  onKit,
  index = 0,
  testIdPrefix = "event",
}) => {
  const { colorFor, forumName } = useForumColors();
  const color = colorFor(event);
  const allDay = isAllDay(event);
  const travel = event.event_type === "out_of_station";

  return (
    <button
      type="button"
      data-testid={`${testIdPrefix}-${event.event_id}`}
      onClick={() => onClick?.(event)}
      className="w-full text-left flex gap-4 items-start p-4 border border-border rounded-sm bg-card hover:bg-accent/40 transition-colors duration-200 rise"
      style={{
        animationDelay: `${Math.min(index, 8) * 45}ms`,
        borderLeft: `6px solid ${color}`,
        backgroundImage: allDay
          ? `repeating-linear-gradient(135deg, ${alpha(color, 0.18)}, ${alpha(color, 0.18)} 6px, transparent 6px, transparent 14px)`
          : undefined,
      }}
    >
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2 mb-1.5">
          <span
            className="chip"
            style={{ color, background: alpha(color, 0.12), borderColor: alpha(color, 0.45) }}
          >
            {allDay ? (
              travel ? (
                <Plane className="h-3.5 w-3.5" />
              ) : (
                <Sun className="h-3.5 w-3.5" />
              )
            ) : null}
            {typeLabel(event.event_type)}
          </span>
          {event.start_time ? (
            <span className="inline-flex items-center gap-1 text-base font-semibold">
              <Clock className="h-4 w-4" /> {timeRange(event)}
            </span>
          ) : null}
          {event.session_flag ? (
            <span className="text-sm font-medium text-muted-foreground">
              {sessionLabel(event.session_flag)}
            </span>
          ) : null}
          {event.urgent ? (
            <span className="inline-flex items-center gap-1 text-fixed text-sm font-semibold">
              <AlertTriangle className="h-4 w-4" /> Urgent
            </span>
          ) : null}
          {event.clash ? (
            <span
              data-testid={`clash-badge-${event.event_id}`}
              className="chip chip-fixed"
              title="Another fixed slot is within 30 minutes — this entry is still saved"
            >
              <GitFork className="h-3.5 w-3.5" /> Clash
            </span>
          ) : null}
        </span>
        <span className="block text-lg font-semibold leading-snug break-words">{event.title}</span>
        <span className="mt-2 flex flex-wrap gap-2">
          {event.vc_link ? (
            <Action
              testId={`event-join-${event.event_id}`}
              label={`Join ${event.vc_platform || "video call"}`}
              icon={Video}
              onPick={() => window.open(event.vc_link, "_blank", "noopener")}
            />
          ) : null}
          {onVoiceLog ? (
            <Action
              testId={`event-voice-log-${event.event_id}`}
              label="Voice log"
              icon={Mic}
              onPick={() => onVoiceLog(event)}
            />
          ) : null}
          {onKit ? (
            <Action
              testId={`event-kit-${event.event_id}`}
              label="Day kit"
              icon={BookOpenCheck}
              onPick={() => onKit(event)}
            />
          ) : null}
        </span>
        <span className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
            {forumName(event)}
          </span>
          {event.case_number ? <span>{event.case_number}</span> : null}
          {event.location ? (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {event.location}
            </span>
          ) : null}
          {!event.matter_id ? (
            <span className="inline-flex items-center gap-1 text-foreground/70">
              <FileText className="h-3.5 w-3.5" /> Placeholder — unassigned
            </span>
          ) : null}
        </span>
        {event.listing_details ? (
          <span className="mt-1 block text-sm text-foreground">
            <span className="font-semibold">Listing: </span>
            <Markdown
              text={event.listing_details}
              inline
              testId={`event-listing-details-${event.event_id}`}
            />
          </span>
        ) : null}
      </span>
    </button>
  );
};
